import type {
  CompetitionOnTheDay,
  CompEvent,
  IndividualEntry,
  IndividualResult,
  EntryGender,
  HeatNoString,
  Team,
  TeamAthlete,
  Seeding,
  CompTrialResults,
  AthleteTrialMap,
  TrialNo
} from '@/components/results/store-comp-results'
import * as ResultsService from '@/components/results/results-service'
import type { AthleteIdString, EventGroupIdString } from '@/services/common'
import type {
  ApiCompetitionAthlete,
  ApiCompetitionEntry,
  ApiCompetitionOnTheDay,
  ApiCompetitionResult,
  ApiCompetitionSchedule,
  ApiCompetitionScheduleOptions,
  ApiCompetitionTeam,
  ApiCompetitionTrial
} from '@/components/results/middleware/competition-converter.models'
import { convertArrayToObject, convertArrayToObjectArray } from '@/services/common-service-utils'

/**
 * Converts external JSON payload (617.json) to CompetitionOnTheDay format
 */
export function convertExternalJsonToCompetitionOnTheDay(
  apiCompetitionOnTheDay: ApiCompetitionOnTheDay
): CompetitionOnTheDay {
  const competition = ResultsService.factoryCompetitionOnTheDay()

  // Set basic competition properties
  competition.id = apiCompetitionOnTheDay.competition.compId
  competition.name = apiCompetitionOnTheDay.competition.compName
  competition.date = apiCompetitionOnTheDay.competition.compdate
  competition.location = apiCompetitionOnTheDay.competition.locName
  competition.organiser = apiCompetitionOnTheDay.competition.organiser.trim()
  competition.logo = apiCompetitionOnTheDay.competition.logo
  // competition.checkIn = !!externalJson.competition.
  // competition.domain = externalJson.competition. || ''

  const apiOptions =
    typeof apiCompetitionOnTheDay.competition.options === 'string'
      ? JSON.parse(apiCompetitionOnTheDay.competition.options)
      : apiCompetitionOnTheDay.competition.options

  competition.options = {
    ...ResultsService.factoryCompetitionOptions(),
    ...apiOptions,
    cardInfo: {
      ...ResultsService.factoryCompetitionOptions().cardInfo,
      ...(apiOptions?.cardInfo ?? {})
    }
  }
  competition.cachedDataSummary = apiCompetitionOnTheDay.cachedDataSummary

  // Convert events
  competition.events = convertSchedules(apiCompetitionOnTheDay)

  competition.trialResults = convertTrials(apiCompetitionOnTheDay.trials)

  return competition
}

/**
 * Converts external events format to CompEvent format
 */
function convertSchedules(
  apiCompetitionOnTheDay: ApiCompetitionOnTheDay
): Record<EventGroupIdString, CompEvent> {
  const events: Record<EventGroupIdString, CompEvent> = {}

  const schedules = apiCompetitionOnTheDay.schedules

  const entriesByEgId = convertArrayToObjectArray('egId', apiCompetitionOnTheDay.entries)
  const resultsByEgId = convertArrayToObjectArray('egId', apiCompetitionOnTheDay.results)
  const teamsByEgId = convertArrayToObjectArray('egId', apiCompetitionOnTheDay.teams)
  // const athletesById = convertArrayToObjectArray('id', apiCompetitionOnTheDay.athletes)
  // don't want array
  const athletesById = convertArrayToObject('id', apiCompetitionOnTheDay.athletes)

  //  Using reduce loop through all the schedules extract the egId select
  //  base properties then assign entries results in teams if found
  schedules.reduce((acc, schedule) => {
    const egId = schedule.egId.toString()

    const scheduledEvent = convertSchedule(schedule)
    scheduledEvent.entries = convertEntries(entriesByEgId[egId], athletesById)
    scheduledEvent.results = convertResults(resultsByEgId[egId])
    scheduledEvent.teams = convertTeams(teamsByEgId[egId])

    acc[egId] = scheduledEvent
    return acc
  }, events)

  return events
}

/**
 *
 * @param apiCompetitionSchedule
 */
export function convertSchedule(apiCompetitionSchedule: ApiCompetitionSchedule): CompEvent {
  // if options are string parse
  const egOptions: ApiCompetitionScheduleOptions =
    typeof apiCompetitionSchedule.egOptions === 'string'
      ? JSON.parse(apiCompetitionSchedule.egOptions)
      : apiCompetitionSchedule.egOptions

  /*
  // Bullshit hack 1.  Adjust eventStartDate back one hour.
  const eventStartDate = new Date(apiCompetitionSchedule.eventStartDate)

  // take 1 hr off
  eventStartDate.setHours(eventStartDate.getHours() - 1)
  */

  // If eventStartDate ends with 'Z' it is UTC time, BUT the time is actually local time.
  // So we need to remove the 'Z' and treat as local time.
  let eventStartDateString = apiCompetitionSchedule.eventStartDate
  if (eventStartDateString.endsWith('Z')) {
    eventStartDateString = eventStartDateString.slice(0, -1)
  }

  return {
    egId: apiCompetitionSchedule.egId,
    name: apiCompetitionSchedule.eventName,
    eventDefName: apiCompetitionSchedule.eventDefName || apiCompetitionSchedule.eventName,
    eventNo: apiCompetitionSchedule.eventNo,
    typeNo: apiCompetitionSchedule.typeNo,
    eventDate: eventStartDateString, // eventStartDate.toISOString(),
    egOptions: egOptions,
    entries: {},
    results: {},
    teams: []
  }
}

/**
 * Converts external entries format to IndividualEntry format
 */
export function convertEntries(
  apiCompetitionEntries: ApiCompetitionEntry[],
  athletesById: Record<AthleteIdString, ApiCompetitionAthlete>
): Record<AthleteIdString, IndividualEntry> {
  // Filter out any API competition entries where the athlete ID is null
  // apiCompetitionEntries = apiCompetitionEntries.filter((entry) => entry.athleteId !== null)

  if (!apiCompetitionEntries) {
    return {}
  }

  // set to a const, use reduce to return the object, use URN as key
  return apiCompetitionEntries.reduce(
    (acc, entry) => {
      // if (entry.athleteId === null) {
      //   return acc
      // }
      const athlete = athletesById[entry.athleteId as number]
      acc[entry.athleteId as AthleteIdString] = convertEntry(entry, athlete)
      return acc
    },
    {} as Record<AthleteIdString, IndividualEntry>
  )
}

/**
 *
 * @param externalEntry
 * @param athlete
 */
export function convertEntry(
  externalEntry: ApiCompetitionEntry,
  athlete?: ApiCompetitionAthlete
): IndividualEntry {
  let athleteId = externalEntry.athleteId || -1
  if (typeof athleteId === 'string') {
    athleteId = parseInt(athleteId)
  }

  return {
    entryId: externalEntry.entryId || 0,
    athleteId: athleteId,
    URN: athlete && athlete.urn ? athlete.urn.toString() : '',
    firstName: athlete?.firstName || '',
    surName: athlete?.surName || '',
    aoCode: externalEntry.aoCode || (athlete ? athlete.aoCode : ''),
    perf: externalEntry.pb || '',
    eventAgeGroup: externalEntry.ageGroupName || '',
    ageGroup: externalEntry.ageGroupName || '',
    bibNo: externalEntry.athleteBibNo ? externalEntry.athleteBibNo.toString() : '',
    teamBibNo: externalEntry.teamBibNo || '',
    clubName: externalEntry.athleteClub,
    gender: athlete ? athlete.gender : ('' as EntryGender),
    seeding: {
      heatNo: externalEntry.heatNo || 0,
      laneNo: externalEntry.laneNo || 0
    },
    pof10: undefined, // TODO,
    present: externalEntry.present,
    collected: externalEntry.collected ?? 0,
    checkedIn: externalEntry.checkedIn,
    heatNoCheckedIn: externalEntry.heatNoCheckedIn,
    laneNoCheckedIn: externalEntry.laneNoCheckedIn,
    classification: externalEntry.classification,
    periodStart: externalEntry.periodStart,
    isWaitlisted: externalEntry.isWaitlisted,
    waitlistPosition: externalEntry.waitlistPosition
  }
}

/**
 * Converts external results format to IndividualResult format
 */
export function convertResults(
  externalResults: ApiCompetitionResult[]
): Record<HeatNoString, IndividualResult[]> {
  const results: Record<HeatNoString, IndividualResult[]> = {}

  if (!externalResults) {
    return results
  }

  // Use reduce to return this data set
  const resultsReduced = externalResults.reduce(
    (acc, result) => {
      const heatNo = result.heatNo.toString()
      if (!acc[heatNo]) {
        acc[heatNo] = []
      }
      acc[heatNo].push(convertResult(result))
      return acc
    },
    {} as Record<HeatNoString, IndividualResult[]>
  )

  return resultsReduced
}

/**
 *
 * @param apiCompetitionResult
 */
export function convertResult(apiCompetitionResult: ApiCompetitionResult): IndividualResult {
  return {
    laneNo: apiCompetitionResult.laneNo || 0,
    position: apiCompetitionResult.position || 0,
    wind: apiCompetitionResult.wind || '',
    athlete: apiCompetitionResult.athlete || '',
    athleteId: apiCompetitionResult.athleteId || 0,
    affilation: apiCompetitionResult.club || '',
    bibNo: apiCompetitionResult.bibNo || '',
    score: apiCompetitionResult.score || '',
    scoreText: apiCompetitionResult.scoreText || '',
    qualify: apiCompetitionResult.qualify || null,
    animateThisAthlete: undefined,
    fieldResultAthlete: undefined,
    rhOptions: apiCompetitionResult.rhOptions
      ? JSON.parse(apiCompetitionResult.rhOptions)
      : undefined,
    ageGroup: apiCompetitionResult.ageGroup || ''
  }
}

export function convertTeams(apiCompetitionTeams: ApiCompetitionTeam[]): Team[] {
  if (!apiCompetitionTeams) {
    return []
  }

  return apiCompetitionTeams.map((team) => {
    return convertTeam(team)
  })
}

export function convertTeam(apiCompetitionTeam: ApiCompetitionTeam): Team {
  return {
    athletes: [], // TODO
    id: apiCompetitionTeam.entryId, // TODO
    teamName: apiCompetitionTeam.teamName,
    teamBibNo: apiCompetitionTeam.teamBibNo,
    egId: apiCompetitionTeam.egId,
    seeding:
      apiCompetitionTeam.heatno != null && apiCompetitionTeam.laneno != null
        ? { heatNo: apiCompetitionTeam.heatno, laneNo: apiCompetitionTeam.laneno }
        : undefined,
    present: apiCompetitionTeam.present as 1 | 0 | null
  }
}

/**
 * @param apiCompetitionTrials
 *
 * @returns E.g. {
 *   "16070": Object {
 *     "198072": Object {
 *       "1.50:1": "O",
 *       "1.55:1": "X",
 *       "1.55:2": "O",
 *       "1.60:1": "O",
 *       "1.65:1": "X",
 *       "1.65:2": "O",
 *       "1.70:1": "O",
 *       "1.75:1": "O",
 *       "1.80:1": "O",
 *       "1.85:1": "X",
 *       "1.85:2": "X",
 *       "1.85:3": "O",
 *     },
 *   },
 *   "16071": Object {
 *     "108858": Object {
 *       "t1": "22",
 *     },
 *     "156396": Object {
 *       "t1": "11",
 *     },
 *   },
 * }

 */
export function convertTrials(apiCompetitionTrials: ApiCompetitionTrial[]): CompTrialResults {
  if (!apiCompetitionTrials) {
    return {}
  }

  return apiCompetitionTrials.reduce((acc, trial) => {
    const egId = trial.egId.toString()
    const athleteId = trial.athleteId.toString()
    const resultKey = trial.resultKey
    const resultValue = trial.resultValue

    // create entry for egId
    if (!acc[egId]) {
      acc[egId] = {}
    }

    // create entry for athleteId
    if (!acc[egId][athleteId]) {
      acc[egId][athleteId] = {}
    }

    // AthleteTrialMap
    // const athleteTrialMap: AthleteTrialMap = acc[egId][athleteId]
    // athleteTrialMap[resultKey as TrialNo] = resultValue
    acc[egId][athleteId][resultKey as TrialNo] = resultValue

    return acc
  }, {} as CompTrialResults)
}

/**
 * Utility function to load and convert external JSON file
 */
export async function loadAndConvertExternalJson(url: string): Promise<CompetitionOnTheDay> {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`)
    }
    const data = await response.json()
    return convertExternalJsonToCompetitionOnTheDay(data)
  } catch (error) {
    console.error('Error loading and converting external JSON:', error)
    return ResultsService.factoryCompetitionOnTheDay()
  }
}
